
import {Options, Vue} from 'vue-class-component';
import ProfileService from "@/services/ProfileService";
import {Credit} from "@/data/credits";
import {formatDatetime, parseApiTimestamp} from "@/utils/helpers";
import {differenceInDays, differenceInMinutes, isPast} from "date-fns";
import EventBusService from "@/services/EventBusService";


@Options({
    props: {
        regionId: {
            type: Number,
            required: true,
        }
    },
    methods: {
        formatDatetime,
    },
    components: {},
    watch: {
        credits() {
            const trialCredit = this.unusedTrialCredit;
            EventBusService.$emit('user.credits.hasTrial:changed', {
                hasTrial: !!trialCredit,
                regionLinkable: trialCredit?.region_linkable || undefined,
            });
        },
    },
})
export default class ProfileRegionCredits extends Vue {
    regionId!: number

    loading = true

    credits: Credit[] = []
    expanded = false
    initialVisibleCount = 3

    onExpand() {
        this.expanded = true;
    }

    get unusedTrialCredit(): Credit | null {
        return this.credits.find(credit => credit.type === 2 && credit.atoms.some(atom => atom.used_for === null)) || null;
    }

    usedAtomsCount(credit: Credit): number {
        return credit.atoms.filter(atom => atom.used_for !== null).length;
    }

    nameHuman(credit: Credit) {
        if (credit.type === 2) { // = enum trial
            return 'Probestunde';
        }
        return credit.object_human;
    }

    nameClass(credit: Credit) {
        const isTrial = credit.type === 2 && this.daysUntilExpiration(credit) !== -1;
        return {
            'text-success': isTrial,
            'bf-font-weight-bold': isTrial,
        };
    }

    progress(credit: Credit): number {
        if (credit.object === 0) { // @TODO: enum constant
            const created = parseApiTimestamp(credit.created_at),
                expires = parseApiTimestamp(credit.expires_at);

            if (isPast(expires)) {
                return 1;
            }

            const durationFlatrate = differenceInMinutes(
                created,
                expires
            );

            const durationElapsed = differenceInMinutes(
                created,
                new Date()
            );

            return Math.min(1, durationElapsed / durationFlatrate);
        }

        if (credit.object === 1) {
            return this.usedAtomsCount(credit) / credit.atoms.length;
        }

        if (credit.object === 2) {
            return credit.course ? 1 : 0;
        }

        console.warn('progress of unknown credit object type requested');
        return 0.0;
    }

    progressInverted(credit: Credit): number {
        return 1 - this.progress(credit);
    }

    progressPercentage(credit: Credit, round?: 'round'): string {

        if (this.daysUntilExpiration(credit) === -1) {
            if (round === 'round')
                return '';
            return '100%';
        }

        const calc = this.progress(credit);
        return round === 'round' ? (Math.round(calc * 100) + '%') : (calc * 100 + '%');
    }

    progressPercentageInverted(credit: Credit, round?: 'round'): string {

        if (this.progress(credit) == 0) {
            return '0%';
        }

        const calc = this.progressInverted(credit);
        return round === 'round' ? (Math.round(calc * 100) + '%') : (calc * 100 + '%');
    }

    progressColor(credit: Credit) {
        /*if( credit.object === 0 ) { // @TODO: enum constant
            const daysLeft = this.daysUntilExpiration(credit);
            if( daysLeft === 0 ) {
                return 'bg-danger';
            }
        }*/

        if (this.daysUntilExpiration(credit) === -1) {
            return 'danger';
        }

        const progress = this.progress(credit);

        if (progress < 1.0) {
            return 'success';
        }

        /* if (progress < 1.0) {
             return 'warning';
         }*/

        return 'danger';
    }

    isProgressHidden(credit: Credit): boolean {
        return credit.object === 2 || (credit.object === 0 && this.daysUntilValid(credit) !== -1);
    }

    daysUntilValid(credit: Credit) {
        const valid = parseApiTimestamp(credit.valid_at);

        if (isPast(valid)) {
            return -1;
        }

        return Math.max(0, differenceInDays(
            new Date(),
            valid
        ));
    }

    daysUntilExpiration(credit: Credit) {
        const expires = parseApiTimestamp(credit.expires_at);

        if (isPast(expires)) {
            return -1;
        }

        return Math.max(0, differenceInDays(
            expires,
            new Date()
        ));
    }

    loadCredits() {
        this.loading = true;
        return ProfileService.getCredits(this.regionId).then(result => {
            this.credits = result.data.data.filter((credit: Credit) => credit.region_id === this.regionId).sort((a: Credit, b: Credit) => {
                const aProg = this.progress(a);
                const bProg = this.progress(b);

                /*if( aProg < 1.0 && bProg >= 1.0 ) {
                    return -1;
                }
                if( aProg >= 1.0 && bProg < 1.0 ) {
                    return 1;
                }*/
                if (aProg < bProg) {
                    return -1;
                }
                if (aProg > bProg) {
                    return 1;
                }
                return 0;
            });

            const creditsRunningCount = this.credits.filter(credit => this.progress(credit) < 1).length;
            // calculate row count and multiply with 3 (only consider desktop view)
            this.initialVisibleCount = Math.max(1, (Math.ceil(creditsRunningCount / 3) + (creditsRunningCount % 3 === 0 ? 1 : 0))) * 3;
        }).finally(() => this.loading = false);
    }

    onUserCreditsChanged() {
        this.loadCredits();
    }

    onUserAppointmentEnrollmentsChanged() {
        this.loadCredits();
    }

    mounted() {
        this.loadCredits();

        EventBusService.$on('user.credits:changed', this.onUserCreditsChanged);
        EventBusService.$on('user.appointments.enrollment:changed', this.onUserAppointmentEnrollmentsChanged);
    }

    beforeUnmount() {
        EventBusService.$off('user.credits:changed', this.onUserCreditsChanged);
        EventBusService.$off('user.appointments.enrollment:changed', this.onUserAppointmentEnrollmentsChanged);
    }
}
